import { delegate } from "oj-promise-utils";
import { eases } from "../eases";
import { v4 as uuid } from "uuid";
export class ActorItem {
    input;
    current;
    _timing = { delay: 0, duration: 1000, easing: "linear" };
    _easing;
    _completed = true;
    _started = false;
    _startStamp;
    _delegate;
    constructor(input, timing) {
        this.reset(input, timing);
    }
    reset(input, timing) {
        delete this._startStamp;
        this.input = input;
        if (this._completed) {
            this._delegate = delegate();
            this._completed = false;
        }
        this.timing(timing);
        return this;
    }
    get promise() {
        return this._delegate?.promise ?? Promise.resolve(undefined);
    }
    get completed() {
        return this._completed;
    }
    get started() {
        return this._started;
    }
    set(input, timing) {
        this.reset(input, timing);
        return this;
    }
    merge(input, timing) {
        this.reset(Object.assign(this.input, input), timing);
        return this;
    }
    update(now) {
        if (this._completed)
            return this.current;
        this._startStamp ??= now;
        if (this._timing.delay && now - this._startStamp < this._timing.delay) {
            this.current = this.interpolate(0);
            return this.current;
        }
        const progress = this._timing.duration
            ? Math.min(1, (now - this._startStamp - this._timing.delay) / this._timing.duration)
            : 1;
        const n = this._easing(progress);
        this.current = this.interpolate(n);
        this._started = true;
        if (progress >= 1 && !this._timing.continuous) {
            this._completed = true;
            this._delegate.resolve(undefined);
        }
        return this.current;
    }
    timing(timing) {
        if (timing)
            this._timing = Object.assign(this._timing, timing);
        this._easing = typeof this._timing.easing === "function" ? this._timing.easing : eases[this._timing.easing];
        return this;
    }
}
export class Actor {
    items;
    id = uuid();
    _timing = { delay: 0, duration: 1000, easing: "linear" };
    constructor() {
        this.items = new Set();
    }
    get done() {
        let done = true;
        for (const item of this.items)
            if (!item.completed)
                done = false;
        return done;
    }
    add(input, timing) {
        timing = { ...this._timing, ...(timing ?? {}) };
        const item = this.createItem(input, timing);
        this.items.add(item);
        return item;
    }
    remove(item) {
        this.items.delete(item);
        return this;
    }
    clear() {
        this.items = new Set();
    }
    update(now) {
        this.interpolate(now);
        return this;
    }
    destroy() {
        this.items.clear();
        return this;
    }
    timing(timing) {
        Object.assign(this._timing, timing);
        for (const item of this.items)
            item.timing(this._timing);
        return this;
    }
}
export class ActorType extends Actor {
    createItem(target, timing) {
        return new ActorItemType(target, timing);
    }
    interpolate(now) {
        throw new Error("This class is just for typing, not implemented.");
    }
}
export class ActorItemType extends ActorItem {
    interpolate(n) {
        throw new Error("Method not implemented.");
    }
}
